// OAuthConfiguration.jsx
import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { TaleDashbaordMainCard } from "@components/TaleDashbaordMainCard";
import TaleButton from "@components/TaleButton";
import { TaleInput } from "@components/TaleInput";
import { TaleLoadingFallback } from "@components/TaleLoadingFallback";
import { InternalServerError } from "@components/Errors/InternalServerError";
import { oauthConfigService} from "@/api/oauthConfigService";

const OAuthConfiguration = () => {
    const queryClient = useQueryClient();

    const [formData, setFormData] = useState({
        googleClientId: "",
        googleClientSecret: "",
        facebookClientId: "",
        facebookClientSecret: "",
    });

    const { data: config, isLoading, error } = useQuery({
        queryKey: ["oauth-config"],
        queryFn: oauthConfigService.getConfig,
        retry: false,
    });

    const updateConfigMutation = useMutation({
        mutationFn: (data) => oauthConfigService.updateConfig(data),
        onSuccess: () => {
            queryClient.invalidateQueries(["oauth-config"]);
            alert("Configuration OAuth2 mise à jour avec succès !");
        },
        onError: (error) => {
            console.error("Update error:", error);
            alert("Erreur lors de la mise à jour de la configuration OAuth2");
        },
    });

    useEffect(() => {
        if (config) {
            setFormData({
                googleClientId: config.googleClientId || "",
                googleClientSecret: "",
                facebookClientId: config.facebookClientId || "",
                facebookClientSecret: "",
            });
        }
    }, [config]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = () => {
        const payload = {
            googleClientId: formData.googleClientId?.trim() || "",
            facebookClientId: formData.facebookClientId?.trim() || "",
        };

        if (formData.googleClientSecret?.trim()) {
            payload.googleClientSecret = formData.googleClientSecret.trim();
        }
        if (formData.facebookClientSecret?.trim()) {
            payload.facebookClientSecret = formData.facebookClientSecret.trim();
        }
        updateConfigMutation.mutate(payload);
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <TaleLoadingFallback />
            </div>
        );
    }

    if (error) {
        return <InternalServerError />;
    }

    return (
        <TaleDashbaordMainCard className="p-6">
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900">Configuration OAuth2</h1>
                <p className="text-gray-600 mt-2">
                    Gérez les fournisseurs d'authentification sociale Google et Facebook
                </p>
                <hr />
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-xl border">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-sm text-blue-600 font-medium">Google OAuth</div>
                            <div className="text-xl font-bold text-gray-900 truncate">
                                {config?.googleEnabled ? (
                                    <span className="text-green-600">Activé</span>
                                ) : (
                                    <span className="text-red-500">Désactivé</span>
                                )}
                            </div>
                        </div>
                        <i className="fab fa-google text-3xl text-blue-400"></i>
                    </div>
                    <div className="mt-3 text-sm text-gray-600">
                        Secret :{" "}
                        {config?.googleSecretConfigured ? (
                            <span className="text-green-600 font-medium">Configuré ✓</span>
                        ) : (
                            <span className="text-orange-500 font-medium">Non configuré</span>
                        )}
                    </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-xl border">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-sm text-indigo-600 font-medium">Facebook OAuth</div>
                            <div className="text-xl font-bold text-gray-900">
                                {config?.facebookEnabled ? (
                                    <span className="text-green-600">Activé</span>
                                ) : (
                                    <span className="text-red-500">Désactivé</span>
                                )}
                            </div>
                        </div>
                        <i className="fab fa-facebook text-3xl text-indigo-400"></i>
                    </div>
                    <div className="mt-3 text-sm text-gray-600">
                        Secret :{" "}
                        {config?.facebookSecretConfigured ? (
                            <span className="text-green-600 font-medium">Configuré ✓</span>
                        ) : (
                            <span className="text-orange-500 font-medium">Non configuré</span>
                        )}
                    </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-xl border">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-sm text-gray-500 font-medium">Dernière mise à jour</div>
                            <div className="text-lg font-bold text-gray-900">
                                {config?.createdAt
                                    ? new Date(config.createdAt).toLocaleDateString("fr-FR", {
                                        day: "2-digit",
                                        month: "short",
                                        year: "numeric",
                                    })
                                    : "—"}
                            </div>
                        </div>
                        <i className="fas fa-clock text-3xl text-gray-400"></i>
                    </div>
                    <div className="mt-3 text-sm text-gray-600">Date de configuration</div>
                </div>
            </div>

            {/* Form Section */}
            <div className="space-y-8">

                {/* Google */}
                <div className="border-t pt-8">
                    <div className="flex items-center mb-6">
                        <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center mr-4">
                            <i className="fab fa-google text-blue-500 text-xl"></i>
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">Google OAuth2</h2>
                            <p className="text-gray-600 text-sm">
                                Paramètres de connexion via Google
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <TaleInput
                            label="Google Client ID *"
                            name="googleClientId"
                            value={formData.googleClientId}
                            onChange={handleInputChange}
                            placeholder="Ex: 123456789-abc.apps.googleusercontent.com"
                        />
                        <TaleInput
                            label={
                                config?.googleSecretConfigured
                                    ? "Google Client Secret (laisser vide pour conserver)"
                                    : "Google Client Secret *"
                            }
                            name="googleClientSecret"
                            type="password"
                            value={formData.googleClientSecret}
                            onChange={handleInputChange}
                            placeholder={
                                config?.googleSecretConfigured
                                    ? "••••••••••••••••"
                                    : "Votre secret Google"
                            }
                            helperText={
                                config?.googleSecretConfigured
                                    ? "Secret déjà configuré — laissez vide pour ne pas le modifier"
                                    : undefined
                            }
                        />
                    </div>
                </div>

                {/* Facebook */}
                <div className="border-t pt-8">
                    <div className="flex items-center mb-6">
                        <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center mr-4">
                            <i className="fab fa-facebook text-indigo-500 text-xl"></i>
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">Facebook OAuth2</h2>
                            <p className="text-gray-600 text-sm">
                                Paramètres de connexion via Facebook
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <TaleInput
                            label="Facebook App ID *"
                            name="facebookClientId"
                            value={formData.facebookClientId}
                            onChange={handleInputChange}
                            placeholder="Ex: 1234567890123456"
                        />
                        <TaleInput
                            label={
                                config?.facebookSecretConfigured
                                    ? "Facebook App Secret (laisser vide pour conserver)"
                                    : "Facebook App Secret *"
                            }
                            name="facebookClientSecret"
                            type="password"
                            value={formData.facebookClientSecret}
                            onChange={handleInputChange}
                            placeholder={
                                config?.facebookSecretConfigured
                                    ? "••••••••••••••••"
                                    : "Votre secret Facebook"
                            }
                            helperText={
                                config?.facebookSecretConfigured
                                    ? "Secret déjà configuré — laissez vide pour ne pas le modifier"
                                    : undefined
                            }
                        />
                    </div>
                </div>

                {/* Submit */}
                <div className="flex justify-end pt-2">
                    <TaleButton
                        onClick={handleSubmit}
                        loading={updateConfigMutation.isPending}
                    >
                        Enregistrer la configuration
                    </TaleButton>
                </div>

                {/* Guide Section */}
                <div className="border-t pt-8">
                    <div className="flex items-center mb-6">
                        <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center mr-4">
                            <i className="fas fa-question-circle text-gray-500 text-xl"></i>
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">Guide de configuration</h2>
                            <p className="text-gray-600 text-sm">Comment obtenir vos identifiants OAuth2</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="p-6 bg-gray-50 rounded-xl border">
                            <div className="flex items-center mb-3">
                                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                                    <i className="fab fa-google text-blue-500"></i>
                                </div>
                                <div className="font-semibold text-blue-700">Google Cloud Console</div>
                            </div>
                            <p className="text-sm text-gray-600">
                                Créez un projet sur{" "}
                                <a
                                href="https://console.cloud.google.com/apis/credentials"
                                target="_blank"
                                rel="noreferrer"
                                className="text-blue-500 underline"
                                >
                                    console.cloud.google.com
                                </a>{" "}
                                puis générez des identifiants OAuth 2.0 de type « Application Web ».
                            </p>
                    </div>

                    <div className="p-6 bg-gray-50 rounded-xl border">
                        <div className="flex items-center mb-3">
                            <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center mr-3">
                                <i className="fab fa-facebook text-indigo-500"></i>
                            </div>
                            <div className="font-semibold text-indigo-700">Facebook Developers</div>
                        </div>
                        <p className="text-sm text-gray-600">
                            Créez une application sur{" "}
                            <a
                            href="https://developers.facebook.com/apps"
                            target="_blank"
                            rel="noreferrer"
                            className="text-indigo-500 underline"
                            >
                                developers.facebook.com
                            </a>{" "}
                            et activez le produit « Facebook Login » pour obtenir votre App ID et App Secret.
                        </p>
                </div>

                <div className="p-6 bg-gray-50 rounded-xl border">
                    <div className="flex items-center mb-3">
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mr-3">
                            <i className="fas fa-shield-alt text-green-500"></i>
                        </div>
                        <div className="font-semibold text-green-700">Sécurité des secrets</div>
                    </div>
                    <p className="text-sm text-gray-600">
                        Les secrets ne sont jamais renvoyés par l'API. Laissez le champ vide lors d'une mise à jour pour conserver le secret existant.
                    </p>
                </div>

                <div className="p-6 bg-gray-50 rounded-xl border">
                    <div className="flex items-center mb-3">
                        <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center mr-3">
                            <i className="fas fa-toggle-on text-orange-500"></i>
                        </div>
                        <div className="font-semibold text-orange-700">Activation automatique</div>
                    </div>
                    <p className="text-sm text-gray-600">
                        Un fournisseur est automatiquement activé dès que son Client ID et son Secret sont tous deux configurés.
                    </p>
                </div>
            </div>
        </div>
</div>
</TaleDashbaordMainCard>
);
};

export default OAuthConfiguration;